<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

const props = withDefaults(
    defineProps<{
        src: string
        autoplay?: boolean
        rootMargin?: string
    }>(),
    {
        autoplay: true,
        rootMargin: '300px 0px',
    }
)

const emit = defineEmits<{
    canplay: []
}>()

const videoEl = ref<HTMLVideoElement | null>(null)
const shouldLoad = ref(false)
const isLoaded = ref(false)
let observer: IntersectionObserver | null = null

const handleCanplay = () => {
    isLoaded.value = true
    emit('canplay')
}

onMounted(() => {
    if (!videoEl.value || typeof IntersectionObserver === 'undefined') {
        shouldLoad.value = true
        return
    }

    observer = new IntersectionObserver(
        entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                shouldLoad.value = true
                observer?.disconnect()
                observer = null
            }
        },
        { rootMargin: props.rootMargin }
    )
    observer.observe(videoEl.value)
})

onUnmounted(() => observer?.disconnect())
</script>

<template>
    <video
        ref="videoEl"
        :class="{ 'is-pending': !isLoaded }"
        :src="shouldLoad ? props.src : undefined"
        :autoplay="props.autoplay"
        loop
        muted
        playsinline
        webkit-playsinline
        preload="none"
        crossorigin="anonymous"
        @canplay="handleCanplay"
    />
</template>

<style scoped>
video {
    display: block;
}

.is-pending {
    visibility: hidden;
}
</style>
